/**
 * Test page for comparing XR implementations
 */

import { useState } from 'react'
import { SplatViewerXR } from '@/components/SplatViewer/SplatViewerXR'
import { SplatViewerXROriginal } from '@/components/SplatViewer/SplatViewerXROriginal'

export default function TestXR() {
  // Allow user to input model URL or use a known working one
  const [modelUrl, setModelUrl] = useState('')
  const [useCustomUrl, setUseCustomUrl] = useState(false)
  const [xrVersion, setXrVersion] = useState<'simple' | 'original'>('simple')
  
  // Known test models (you can add more)
  const testModels = [
    {
      name: 'Model 1 (3LFu7rSqP)',
      url: 'https://4dgs-models.moonshine-studio.net/models/3LFu7rSqP-hwzCXmLEcVr/original.splatv'
    },
    {
      name: 'Model 2 (QDu8X2XBR)',
      url: 'https://4dgs-models.moonshine-studio.net/models/QDu8X2XBRaR9znOgx65fc/original.splatv'
    }
  ]
  
  // Test default camera
  const defaultCamera = {
    position: { x: 0, y: 1, z: 3 },
    rotation: { rx: 0, ry: 0, rz: 0 }
  }
  
  if (!modelUrl && !useCustomUrl) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-white mb-4">Test WebXR</h1>
          
          <div className="space-y-4">
            <div>
              <h2 className="text-lg text-gray-300 mb-2">Select XR Service Version:</h2>
              <div className="space-y-2">
                <button
                  onClick={() => setXrVersion('simple')}
                  className={`w-full px-4 py-2 rounded text-left ${
                    xrVersion === 'simple' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <div className="font-semibold">Simplified XR Service</div>
                  <div className="text-sm opacity-80">Reuses splatv-module renderer</div>
                </button>
                <button
                  onClick={() => setXrVersion('original')}
                  className={`w-full px-4 py-2 rounded text-left ${
                    xrVersion === 'original' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <div className="font-semibold">Original XR Service</div>
                  <div className="text-sm opacity-80">Own rendering pipeline</div>
                </button>
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-4">
              <h2 className="text-lg text-gray-300 mb-2">Select a test model:</h2>
              <div className="space-y-2">
                {testModels.map((model) => (
                  <button
                    key={model.url}
                    onClick={() => setModelUrl(model.url)}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-left"
                  >
                    {model.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-4">
              <h2 className="text-lg text-gray-300 mb-2">Or enter custom URL:</h2>
              <input
                type="text"
                placeholder="https://example.com/model.splat"
                className="w-full px-4 py-2 bg-gray-700 text-white rounded mb-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.currentTarget.value) {
                    setModelUrl(e.currentTarget.value)
                    setUseCustomUrl(true)
                  }
                }}
              />
              <p className="text-sm text-gray-400">
                Enter a URL to a .splat or .splatv file and press Enter
              </p>
            </div>
            
            <div className="border-t border-gray-700 pt-4">
              <div className="text-sm text-gray-400">
                <p className="mb-1">Currently selected: <span className="text-white font-semibold">{xrVersion === 'simple' ? 'Simplified' : 'Original'} XR Service</span></p>
                <p>This will affect how XR rendering works when you enter AR/VR mode.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="w-screen h-screen relative">
      {/* Version indicator */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/80 text-white px-4 py-2 rounded">
        Using: <span className={xrVersion === 'simple' ? 'text-green-400' : 'text-blue-400'}>
          {xrVersion === 'simple' ? 'SIMPLIFIED' : 'ORIGINAL'} XR Service
        </span>
      </div>
      
      {/* IMMEDIATE TEST BUTTON - Should appear instantly */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 99999,
        pointerEvents: 'all'
      }}>
        <button
          onClick={() => alert(`TEST BUTTON WORKS! Using ${xrVersion.toUpperCase()} XR Service`)}
          style={{
            padding: '30px 60px',
            fontSize: '24px',
            backgroundColor: xrVersion === 'simple' ? '#00ff00' : '#0088ff',
            color: '#000000',
            border: '3px solid #000000',
            borderRadius: '15px',
            cursor: 'pointer',
            fontWeight: 'bold',
            boxShadow: '0 0 20px rgba(0,255,0,0.5)'
          }}
        >
          TAP ME - {xrVersion.toUpperCase()} XR
        </button>
      </div>
      
      {/* Render appropriate viewer based on selection */}
      {xrVersion === 'simple' ? (
        <SplatViewerXR
          modelUrl={modelUrl}
          defaultCamera={defaultCamera}
          onLoad={() => console.log('Model loaded (simple):', modelUrl)}
          onError={(err) => console.error('Model error (simple):', err)}
        />
      ) : (
        <SplatViewerXROriginal
          modelUrl={modelUrl}
          defaultCamera={defaultCamera}
          onLoad={() => console.log('Model loaded (original):', modelUrl)}
          onError={(err) => console.error('Model error (original):', err)}
        />
      )}
      
      {/* Back button */}
      <button
        onClick={() => {
          setModelUrl('')
          setUseCustomUrl(false)
        }}
        className="absolute top-4 left-4 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
      >
        ← Back
      </button>
    </div>
  )
}